/**
 * Settings > VendorHub (ADR-0024). Company admins are VendorHub admins.
 *   VendorHub                    the on/off switch per company (platform admins) and who uses it
 *   Evidence and review rules    what each kind of data needs, how often vendors are reviewed,
 *                                and how early "running out" starts. Everyone with VendorHub can
 *                                read them (every color traces to a rule); admins change them.
 */
import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { api, type AdminTenant, type Member, type VhRules } from "../../lib/api";
import { useStore } from "../../lib/store";
import { byCoOrder, coName } from "../../lib/brand";
import { Loading, SaveBar, Tile } from "../../components/bits";
import { PartTag } from "./SettingsLayout";
import { CRITICALITY, CRIT_LABEL, DATA_HELP, DATA_LABEL, DATA_TYPES, EVIDENCE_KINDS, EVIDENCE_LABEL, type DataType, type EvidenceKind } from "../../../../shared/vendorhub";

export function VendorHubAccessPage() {
  const { p, me, toast, reload } = useStore();
  const [tenants, setTenants] = useState<AdminTenant[] | null>(null);
  const [members, setMembers] = useState<Record<string, Member[]>>({});
  const [err, setErr] = useState<string | null>(null);
  const load = async () => {
    try {
      const ts = (await api.adminTenants()).tenants.sort(byCoOrder);
      setTenants(ts);
      const on = ts.filter((t) => (t.modules ?? []).includes("vendorhub"));
      const lists = await Promise.all(on.map((t) => api.members(t.code).then((r) => [t.code, r.members] as const, () => [t.code, []] as const)));
      setMembers(Object.fromEntries(lists));
    } catch (e) { setErr((e as Error).message); }
  };
  useEffect(() => { void load(); }, []);
  if (!p.anyAdmin) return <Navigate to="/settings" replace />;
  if (err) return <div className="note bad">{err}</div>;
  if (!tenants) return <Loading />;
  const pa = me.user.is_platform_admin;
  const flip = async (t: AdminTenant, on: boolean) => {
    if (!on && !window.confirm(`Switch VendorHub off for ${t.name}? It disappears for everyone there; nothing is deleted, and switching it back on brings everything back.`)) return;
    try {
      await api.setModules(t.code, { vendorhub: on });
      toast(on ? `VendorHub is on for ${t.name}. Give people VendorHub roles in Settings > Team.` : `VendorHub is off for ${t.name}.`);
      await load(); await reload();
    } catch (e) { toast((e as Error).message); }
  };
  return (
    <div className="view">
      <div className="hgroup"><div>
        <PartTag part="vendorhub" extra="Add-on to the Vault" />
        <h1 className="title">VendorHub</h1>
        <div className="lede">Vendor records with their evidence (SOC reports, insurance, questionnaires), what each vendor touches, reminders before evidence runs out, and a risk rating worked out from rules. It works on its own; with TechHub on, the next release adds each client's vendor list and BAAs.</div>
      </div></div>
      <div className="addons">
        {tenants.map((t) => {
          const on = (t.modules ?? []).includes("vendorhub");
          const ms = members[t.code] ?? [];
          const n = (r: string) => ms.filter((m) => m.roles.includes(r as Member["roles"][number])).length;
          return (
            <section key={t.code} className="card addon">
              <div className="hd">
                <Tile code={t.code} />
                <div><b>{t.name}</b><div className="cap">{on ? "On" : "Off"}</div></div>
                <label className="switch" style={{ marginLeft: "auto" }} title={pa ? "" : "Only platform admins switch add-ons"}>
                  <input type="checkbox" checked={on} disabled={!pa} onChange={(e) => void flip(t, e.target.checked)} aria-label={`VendorHub for ${t.name}`} /><span />
                </label>
              </div>
              {on ? (<>
                <div className="row"><span>VendorHub viewers</span><b>{n("vendorhub_viewer")}</b></div>
                <div className="row"><span>VendorHub editors</span><b>{n("vendorhub_editor")}</b></div>
                <div className="row"><span>Admins (also VendorHub admins)</span><b>{n("admin")}</b></div>
                <div className="row"><Link className="doclink" to={`/settings/people?co=${t.code}`}>Manage who can use it</Link><span /></div>
              </>) : <div className="cap">Turning it on adds VendorHub to the app switcher for the people you give VendorHub roles. Turning it off hides it; nothing is deleted.</div>}
            </section>
          );
        })}
      </div>
      <div className="foot-note">Only platform admins switch add-ons on or off. Viewers see vendors and open evidence files; editors add and change vendors, evidence and findings and record reviews; company admins also change the rules, hand vendors to someone else, retire vendors and remove evidence.</div>
    </div>
  );
}

export function VendorRulesPage() {
  const { p, toast } = useStore();
  const [sp, setSp] = useSearchParams();
  const codes = p.vhCodes;
  const co = codes.includes(sp.get("co") ?? "") ? sp.get("co")! : codes[0] ?? "";
  const [s, setS] = useState<{ rules: VhRules; defaults: VhRules; can_manage: boolean } | null>(null);
  const [draft, setDraft] = useState<VhRules | null>(null);
  const [busy, setBusy] = useState(false);
  const load = () => (co ? api.vhSettings(co).then((r) => { setS(r); setDraft(null); }, (e: Error) => toast(e.message)) : Promise.resolve());
  useEffect(() => { setS(null); void load(); }, [co]);
  if (!codes.length) return <Navigate to="/settings" replace />;
  const r = draft ?? s?.rules;
  const edit = (fn: (x: VhRules) => VhRules) => r && setDraft(fn(JSON.parse(JSON.stringify(r)) as VhRules));
  const changed = !!draft && JSON.stringify(draft) !== JSON.stringify(s?.rules);
  const save = async () => {
    if (!draft) return;
    setBusy(true);
    try { await api.vhSaveSettings(co, draft); toast("Saved. Every vendor's evidence and risk use the new rules now."); await load(); }
    catch (e) { toast((e as Error).message); } finally { setBusy(false); }
  };
  const ro = !s?.can_manage;
  return (
    <div className="view">
      <div className="hgroup"><div>
        <PartTag part="vendorhub" />
        <h1 className="title">Evidence and review rules</h1>
        <div className="lede">What a vendor needs on file because of the data it touches, and how often it's reviewed. Every vendor's evidence state and risk rating come from these rules{ro ? "; company admins change them" : ""}.</div>
      </div></div>
      {codes.length > 1 && <div className="ctabs">{codes.map((c) => <button key={c} className={`ctab ${c === co ? "on" : ""}`} onClick={() => setSp({ co: c }, { replace: true })}><Tile code={c} /><span>{coName(c)}</span></button>)}</div>}
      {!r || !s ? <Loading /> : <>
        <SaveBar n={changed ? 1 : 0} busy={busy} onSave={() => void save()} onUndo={() => setDraft(null)} />
        <div className="card sect">
          <h3>Evidence by data type</h3>
          <div className="cap" style={{ marginBottom: 6 }}>A vendor touching this data needs one of the ticked kinds, no older than the months shown. A report counts from the end of its period; a certificate until it expires.</div>
          {DATA_TYPES.map((d) => {
            const q = r.requirements.find((x) => x.data === d);
            return (
              <div key={d} className="vh-rule">
                <div><span className={`vh-data vh-d-${d}`}>{DATA_LABEL[d]}</span><div className="cap">{DATA_HELP[d]}</div>
                  {!ro && <label className="rh-check" style={{ marginTop: 6 }}><input type="checkbox" checked={!!q} onChange={(e) => edit((x) => ({ ...x, requirements: e.target.checked
                    ? [...x.requirements, s.defaults.requirements.find((y) => y.data === d) ?? { data: d as DataType, kinds: ["questionnaire"], months: 12 }] : x.requirements.filter((y) => y.data !== d) }))} /> Needs evidence</label>}
                </div>
                {q ? <div>
                  <div className="vh-kinds">{EVIDENCE_KINDS.filter((k) => k !== "baa_template" && k !== "other" && k !== "bridge").map((k) => (
                    <label key={k} className="rh-check"><input type="checkbox" disabled={ro} checked={q.kinds.includes(k)} onChange={(e) => edit((x) => ({ ...x, requirements: x.requirements.map((y) => y.data !== d ? y
                      : { ...y, kinds: e.target.checked ? [...y.kinds, k] : y.kinds.filter((z) => z !== k) }) }))} /> {EVIDENCE_LABEL[k as EvidenceKind]}</label>))}</div>
                  <label className="rh-check" style={{ marginTop: 6 }}>No older than <input className="inp inp-sm" style={{ width: 70 }} inputMode="numeric" disabled={ro} value={String(q.months)}
                    onChange={(e) => edit((x) => ({ ...x, requirements: x.requirements.map((y) => (y.data === d ? { ...y, months: Number(e.target.value.replace(/\D/g, "")) || 0 } : y)) }))} /> months</label>
                </div> : <div className="cap">No evidence required.</div>}
              </div>
            );
          })}
          <div className="cap" style={{ marginTop: 8 }}>Business associate agreements are per client and come with client vendor lists (next release).</div>
        </div>
        <div className="card sect">
          <h3>Reviews and warnings</h3>
          {CRITICALITY.map((c) => (
            <div key={c} className="setrow">
              <div className="tx"><b>{CRIT_LABEL[c]} criticality</b><span>Review every</span></div>
              <label className="rh-check"><input className="inp inp-sm" style={{ width: 70 }} inputMode="numeric" disabled={ro} value={String(r.review_months[c])}
                onChange={(e) => edit((x) => ({ ...x, review_months: { ...x.review_months, [c]: Number(e.target.value.replace(/\D/g, "")) || 0 } }))} /> months</label>
            </div>
          ))}
          <div className="setrow">
            <div className="tx"><b>Running out</b><span>Evidence turns amber, and the responsible person gets a task, this many days before it runs out.</span></div>
            <label className="rh-check"><input className="inp inp-sm" style={{ width: 70 }} inputMode="numeric" disabled={ro} value={String(r.warn_days)} onChange={(e) => edit((x) => ({ ...x, warn_days: Number(e.target.value.replace(/\D/g, "")) || 0 }))} /> days</label>
          </div>
          {!ro && <div className="row-btns" style={{ marginTop: 10 }}><button className="linkbtn" onClick={() => setDraft(JSON.parse(JSON.stringify(s.defaults)) as VhRules)}>Put back the starting rules</button></div>}
        </div>
      </>}
    </div>
  );
}
